import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Target } from 'lucide-react';
import { money, monthKey, monthLabel, toMoneyInput } from './financeUtils';
import { financeAPI } from '../../lib/api';

const FIELDS = [['income', 'Ganhos possíveis'], ['fixed', 'Despesas fixas'], ['variable', 'Limite variável']];

const MonthCard = ({ d, item, onChange }) => {
  const key = monthKey(d);
  const reserve = toMoneyInput(item.income) - toMoneyInput(item.fixed) - toMoneyInput(item.variable);
  return (
    <article className="border border-slate-100 rounded-xl p-3 bg-white" data-testid={`challenge-month-${key}`}>
      <h4 className="text-xs font-semibold text-slate-800 mb-2">{monthLabel(d)}</h4>
      <div className="grid grid-cols-3 gap-1.5">
        {FIELDS.map(([f, label]) => (
          <label key={f} className="text-[9px] text-slate-500 leading-tight">
            {label} (R$)
            <input type="number" min="0" step="0.01" inputMode="decimal" placeholder="0,00" value={item[f] || ''}
              onChange={(e) => onChange(key, f, e.target.value)}
              className="mt-1 w-full min-w-0 px-2 py-1.5 border border-slate-200 rounded-lg text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-200"
              data-testid={`challenge-${f}-${key}`} />
          </label>
        ))}
      </div>
      <div className="flex justify-between border-t border-slate-100 mt-2 pt-2 text-[10px] text-slate-500">
        <span>Saldo estimado no mês</span>
        <strong className={reserve < 0 ? 'text-red-600' : 'text-emerald-700'}>{money(reserve)}</strong>
      </div>
    </article>
  );
};

export const ChallengePlanner = ({ initialPlan }) => {
  const [plan, setPlan] = useState(initialPlan || {});
  const timer = useRef(null);
  const months = useMemo(() => { const n = new Date(); return Array.from({ length: 12 }, (_, i) => new Date(n.getFullYear(), n.getMonth() + 1 + i, 1)); }, []);
  useEffect(() => () => clearTimeout(timer.current), []);

  const onChange = (key, field, raw) => {
    setPlan((prev) => {
      const next = { ...prev, [key]: { ...(prev[key] || {}), [field]: raw === '' ? '' : toMoneyInput(raw) } };
      clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        const clean = Object.fromEntries(Object.entries(next).map(([k, v]) => [k, Object.fromEntries(Object.entries(v).map(([a, b]) => [a, toMoneyInput(b)]))]));
        financeAPI.saveChallenge(clean).catch(() => {});
      }, 700);
      return next;
    });
  };

  const sum = (f) => months.reduce((s, d) => s + toMoneyInput((plan[monthKey(d)] || {})[f]), 0);
  const inc = sum('income'); const fix = sum('fixed'); const vari = sum('variable'); const res = inc - fix - vari;

  return (
    <section className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4" id="desafio" data-testid="finance-challenge">
      <h3 className="font-semibold text-slate-900 text-sm">Desafio: planeje os próximos 12 meses</h3>
      <p className="text-[11px] text-slate-500">Defina ganhos possíveis, despesas fixas e um limite para os gastos variáveis. Seu plano é salvo na sua conta.</p>
      <span className="inline-block mt-2 text-[11px] font-bold text-amber-800 bg-amber-50 rounded-lg px-2.5 py-1">Meta de referência: 80%</span>
      <p className="mt-3 text-[11px] text-slate-600 bg-emerald-50/60 rounded-xl p-3 flex gap-2">
        <Target className="w-4 h-4 text-emerald-600 shrink-0" />
        <span><strong>Seu objetivo:</strong> alcançar pelo menos 80% do planejamento. Nesta etapa, você organiza suas metas.</span>
      </p>
      <div className="grid grid-cols-2 gap-2 my-3" data-testid="challenge-summary">
        {[['Ganhos planejados', inc], ['Despesas fixas', fix], ['Limite variável', vari]].map(([l, v]) => (
          <div key={l} className="border border-slate-100 rounded-xl p-2.5"><p className="text-[10px] text-slate-500">{l}</p><p className="text-sm font-bold">{money(v)}</p></div>
        ))}
        <div className="border border-emerald-100 bg-emerald-50/60 rounded-xl p-2.5">
          <p className="text-[10px] text-slate-500">Saldo estimado em 12 meses</p>
          <p className={`text-sm font-bold ${res < 0 ? 'text-red-600' : 'text-emerald-700'}`} data-testid="challenge-reserve-total">{money(res)}</p>
        </div>
      </div>
      <div className="space-y-2">
        {months.map((d) => <MonthCard key={monthKey(d)} d={d} item={plan[monthKey(d)] || {}} onChange={onChange} />)}
      </div>
      <p className="text-[10px] text-slate-400 mt-2">Saldo estimado = ganhos planejados − despesas fixas − limite variável. Os valores são projeções e não incluem rendimentos.</p>
    </section>
  );
};
